import { useToast } from "../components/MessageBox";
import { useTranslation } from "./useTranslation";
import { firstError } from "../api/envelope";

const pageWhitelist = [
  "/login",
  "/register",
  "/",
  "/oauth",
  "/account-switcher",
  "/jobs",
  "/impressum",
  "/imprint",
  "/datenschutz",
  "/privacy",
  "/security",
  "/sicherheit",
  "/tutorial",
];

const DEFAULT_TIMEOUT_MS = 30_000;

// Only 503 (Service Unavailable, emitted by the LB during deploy / cold-start) bounces
// every page to the maintenance screen. 500 is "real bug, controller threw" and should
// keep the user on the page so they see the error toast and can retry / report it --
// not get hidden behind a "we'll be right back".
function shouldRedirectToMaintenance(status: number): boolean {
  return status === 503;
}

function redirectToMaintenance() {
  if (window.location.pathname === '/maintenance') return;
  const params = new URLSearchParams();
  params.set('return', window.location.pathname + window.location.search);
  window.location.href = `/maintenance?${params.toString()}`;
}

function withTimeout(options: RequestInit, timeoutMs: number): { fetchOptions: RequestInit; cleanup: () => void } {
  if (options.signal) {
    return { fetchOptions: options, cleanup: () => { } };
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return {
    fetchOptions: { ...options, signal: controller.signal },
    cleanup: () => clearTimeout(timer),
  };
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

export function getCookie(name: string): string | undefined {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let c of ca) {
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return undefined;
}

function setCookie(name: string, value: string, days: number, path?: string) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ''}${expires}; path=${path || '/'}`;
}

function applyDefaults(options?: RequestInit): RequestInit {
  const headers = new Headers(options?.headers);

  const isFormData = typeof FormData !== 'undefined' && options?.body instanceof FormData;
  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  const csrfToken = getCookie('token');
  if (csrfToken) {
    headers.set('X-CSRF-Token', csrfToken);
  }

  return { ...options, headers };
}

// A few routes stay unwrapped because shipped Java clients parse them directly
// (JarDownload/Versions). Spreading a top-level array would turn it into {0: ..., 1: ...},
// so pass the array through and hang the envelope fields off it instead.
// A 403 body is the only place the backend says which permission was missing, and it is
// allowed to be empty, so a parse failure must not mask the status itself.
async function readJsonBody(response: Response): Promise<any | undefined> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

function normalizeBody(data: any, httpStatus: number) {
  if (Array.isArray(data)) {
    return Object.assign(data, { status: httpStatus, ok: httpStatus >= 200 && httpStatus < 300 });
  }
  const status = data.status ?? data.Status ?? 0;
  return { ...data, status, ok: status === 200 };
}

function persistCsrfToken(response: Response) {
  if (response.headers.has('X-CSRF-Token')) {
    const token = response.headers.get('X-CSRF-Token');
    if (token) setCookie('token', token, 365, "/KLRtho3xkIhCp1pQc3yMSNe6VKmWfw1KdLnxOIOe");
  }
}

export async function sfetchStandalone(url: string, options?: RequestInit & { timeoutMs?: number }) {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...rest } = options ?? {};
  const baseOptions = applyDefaults(rest);
  const { fetchOptions, cleanup } = withTimeout(baseOptions, timeoutMs);

  try {
    let response = await fetch(url, fetchOptions);

    if (response.status === 401) {
      await new Promise(r => setTimeout(r, 100));
      response = await fetch(url, fetchOptions);
    }

    persistCsrfToken(response);

    if (shouldRedirectToMaintenance(response.status)) {
      redirectToMaintenance();
      return { ok: false, status: response.status };
    }

    if (response.status === 401 &&
      pageWhitelist.indexOf(window.location.pathname) === -1 &&
      window.location.pathname.indexOf("oauth") === -1) {
      window.location.href = "/login";
      return { ok: false, status: 401 };
    }

    return normalizeBody(await response.json(), response.status);
  } catch (err) {
    if (isAbortError(err)) {
      return { ok: false, status: 0, errors: { Error: ['Request timed out'] } };
    }
    throw err;
  } finally {
    cleanup();
  }
}

export function useSFetch() {
    const toast = useToast();
    const { t } = useTranslation();

    return async function sfetch(url: string, options?: RequestInit & { timeoutMs?: number }) {
      const { timeoutMs = DEFAULT_TIMEOUT_MS, ...rest } = options ?? {};
      const baseOptions = applyDefaults(rest);
      const { fetchOptions, cleanup } = withTimeout(baseOptions, timeoutMs);

      try {
        let response = await fetch(url, fetchOptions);

        // 401 may race with a concurrent response's Set-Cookie refresh; retry once after a tick.
        if (response.status === 401) {
          await new Promise(r => setTimeout(r, 100));
          response = await fetch(url, fetchOptions);
        }

        persistCsrfToken(response);

        if (shouldRedirectToMaintenance(response.status)) {
          redirectToMaintenance();
          return { ok: false, status: response.status };
        }

        if (response.status === 401) {
          const onPublicPage =
            pageWhitelist.indexOf(window.location.pathname) !== -1 ||
            window.location.pathname.indexOf("oauth") !== -1;
          if (onPublicPage) {
            return { ok: false, status: 401 };
          }
          window.location.href = "/login";
          toast?.error(t('SESSION_LOGIN_REQUIRED'));
          return { ok: false, status: 401 };
        }
        if (response.status === 428 && window.location.pathname !== "/verifysession" && window.location.pathname !== "/me") {
          window.location.href = "/verifysession";
          toast?.error(t('SESSION_VERIFY_REQUIRED'));
          return { status: 428, ok: false };
        }
        if (response.status === 403 && window.location.pathname !== "/forbidden") {
          const body = await readJsonBody(response);
          // Every api.* call reaches here, and the caller toasts the denial with the operation it
          // belongs to. Only speak up when the body carried nothing for the caller to show.
          if (!firstError(body)) toast?.error(t('SESSION_FORBIDDEN'));
          return { ...normalizeBody(body ?? {}, 403), status: 403, ok: false };
        }
        return normalizeBody(await response.json(), response.status);
      } catch (err) {
        if (isAbortError(err)) {
          const timedOut = t('REQUEST_TIMED_OUT');
          toast?.error(timedOut);
          return { ok: false, status: 0, errors: { Error: [timedOut] } };
        }
        throw err;
      } finally {
        cleanup();
      }
  }
}
